import React from "react";
import {
  Target,
  CheckCircle,
  Circle,
  MinusCircle,
} from "@phosphor-icons/react";
import { makeT } from "@/lib/i18n";

function statusStyle(status) {
  if (status === "matched")
    return {
      bg: "bg-[color:var(--brand)]/10",
      text: "text-[color:var(--brand)]",
      icon: CheckCircle,
    };
  if (status === "partial")
    return {
      bg: "bg-[color:var(--score-average)]/15",
      text: "text-[color:var(--score-average)]",
      icon: Circle,
    };
  return {
    bg: "bg-[color:var(--score-bad)]/10",
    text: "text-[color:var(--score-bad)]",
    icon: MinusCircle,
  };
}

function statusLabel(status, t) {
  if (status === "matched") return t("jm.statusMatch");
  if (status === "partial") return t("jm.statusPartial");
  return t("jm.statusGap");
}

/**
 * Job-match preview card. Renders required-skill coverage with a % score.
 * Fully localized via i18n (en / pt-br) and reads pt_br_evidence when
 * available. Used on the pre-interview upload screen (no interview answers
 * are factored into the score — this is a docs-only match).
 */
export function JobMatchCard({ match, lang = "en", testId = "job-match-card" }) {
  const t = makeT(lang);
  if (!match || !match.total) return null;
  const usePt = lang !== "en";
  const scoreColorVar =
    match.score >= 75
      ? "var(--score-good)"
      : match.score >= 50
      ? "var(--score-average)"
      : "var(--score-bad)";
  const gapWord = match.missing === 1 ? t("jm.gap") : t("jm.gaps");
  const ptEvidences = match.pt_br_evidence || [];
  const ptSkills = match.pt_br_skills || [];

  return (
    <section data-testid={testId}>
      <div className="flex items-start justify-between mb-6 gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[color:var(--brand)]">
            <Target weight="duotone" size={22} />
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--text-primary)]">
              {t("jm.title")}
            </h2>
          </div>
          <p className="mt-1 text-sm text-[color:var(--text-secondary)] max-w-xl">
            {t("jm.subtitle")}
          </p>
        </div>
        <div className="text-right" data-testid={`${testId}-score`}>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">
            {t("jm.matchScore")}
          </div>
          <div className="mono text-5xl font-semibold" style={{ color: scoreColorVar }}>
            {match.score}
          </div>
          <div className="text-xs text-[color:var(--text-secondary)] mono">
            {match.matched} {t("jm.matched")} · {match.partial} {t("jm.partial")} · {match.missing} {gapWord}
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-[color:var(--border-stone)] bg-white p-8">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-4">
          {t("jm.covered", match.matched + match.partial, match.total)}
        </div>
        <div className="h-1.5 w-full rounded-full bg-[color:var(--border-stone)] overflow-hidden mb-8">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${match.score}%`, background: scoreColorVar }}
          />
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {match.skills.map((s, i) => {
            const st = statusStyle(s.status);
            const Icon = st.icon;
            const evidence = usePt && ptEvidences[i] ? ptEvidences[i] : s.evidence;
            const skillName = usePt && ptSkills[i] ? ptSkills[i] : s.skill;
            return (
              <li
                key={i}
                data-testid={`${testId}-skill-${i}`}
                className="flex items-start gap-4 p-4 rounded-2xl border border-[color:var(--border-stone)]"
              >
                <div className={`h-9 w-9 shrink-0 rounded-full ${st.bg} ${st.text} flex items-center justify-center`}>
                  <Icon weight="fill" size={18} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold tracking-tight">{skillName}</span>
                    <span className={`text-[10px] uppercase tracking-widest ${st.text}`}>
                      · {statusLabel(s.status, t)}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-[color:var(--text-secondary)] leading-snug">
                    {evidence}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
